const MS_24H = 24 * 60 * 60 * 1000;

const MARKETPLACE_TIMEZONES = {
  IN: 'Asia/Kolkata',
  US: 'America/Los_Angeles',
};

function getMarketplaceTimeZone(marketplace) {
  return MARKETPLACE_TIMEZONES[marketplace] || MARKETPLACE_TIMEZONES.IN;
}

function getCalendarPartsInTimeZone(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);

  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;

  if (!year || !month || !day) {
    return null;
  }

  return { year: Number(year), month: Number(month), day: Number(day) };
}

function getTimeZoneOffsetMs(timeZone, date) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const values = {};

  for (const part of parts) {
    if (part.type !== 'literal') {
      values[part.type] = part.value;
    }
  }

  const asUtc = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second)
  );

  return asUtc - date.getTime();
}

function zonedTimeToUtc(year, month, day, hour, minute, second, millisecond, timeZone) {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second, millisecond);
  const offset = getTimeZoneOffsetMs(timeZone, new Date(utcGuess));

  return utcGuess - offset;
}

function getShipByDayBounds(shipByDate, marketplace = 'IN') {
  const shipBy = new Date(shipByDate);

  if (Number.isNaN(shipBy.getTime())) {
    return null;
  }

  const timeZone = getMarketplaceTimeZone(marketplace);
  const calendar = getCalendarPartsInTimeZone(shipBy, timeZone);

  if (!calendar) {
    return null;
  }

  const { year, month, day } = calendar;
  const dayEnd = zonedTimeToUtc(year, month, day, 23, 59, 59, 999, timeZone);

  return { dayEnd };
}

export function isOrderUnshipped(order) {
  const items = order?.items || [];

  if (!items.length) {
    return true;
  }

  return items.some(
    (item) => Number(item.quantityShipped ?? 0) < Number(item.quantityOrdered ?? 1)
  );
}

export function getOrderProductLabel(order) {
  const firstItem = order.items?.[0];
  const title = firstItem?.title?.trim();

  if (title) {
    return title.length > 72 ? `${title.slice(0, 72)}…` : title;
  }

  return order.amazonOrderId || 'Unshipped order';
}

export function getShipByUrgency(order, now = Date.now()) {
  if (!order.shipByDate || !isOrderUnshipped(order)) {
    return null;
  }

  const bounds = getShipByDayBounds(order.shipByDate, order.marketplace);

  if (!bounds) {
    return null;
  }

  const { dayEnd } = bounds;
  const msUntilDeadline = dayEnd - now;

  if (msUntilDeadline <= 0) {
    return {
      type: 'overdue',
      hoursLeft: 0,
      orderId: order.id,
      label: getOrderProductLabel(order),
      amazonOrderId: order.amazonOrderId,
      shipByDate: order.shipByDate,
    };
  }

  if (msUntilDeadline <= MS_24H) {
    const hoursLeft = Math.min(
      24,
      Math.max(1, Math.ceil(msUntilDeadline / (60 * 60 * 1000)))
    );

    return {
      type: 'urgent',
      hoursLeft,
      orderId: order.id,
      label: getOrderProductLabel(order),
      amazonOrderId: order.amazonOrderId,
      shipByDate: order.shipByDate,
    };
  }

  return null;
}

export function getUrgentUnshippedOrders(orders, now = Date.now()) {
  return (orders || [])
    .map((order) => getShipByUrgency(order, now))
    .filter(Boolean)
    .sort((a, b) => {
      if (a.type === b.type) {
        return a.hoursLeft - b.hoursLeft;
      }

      return a.type === 'overdue' ? -1 : 1;
    });
}

export function getUrgencyRowClassName(urgency) {
  if (!urgency) {
    return '';
  }

  if (urgency.type === 'overdue') {
    return 'bg-red-100/90 ring-1 ring-inset ring-red-300';
  }

  return 'bg-red-50 ring-1 ring-inset ring-red-200';
}
